<template>
	<div class="searchFilter mb-2">
		<div>
			<o-field v-for="(row, i) in rows" :key="i" style="width: 100%">
				<template v-slot:label>{{ row.key?.Text }}</template>
				<o-inputitems
					style="width: 100%"
					v-model="rows[i].values"
					:data="inputOptions"
					field="Text"
					:debounceTyping="300"
					open-on-focus
					autocomplete
					expanded
					@add="AddInputItems($event, row)"
					@focus="FocusInputItems(row)"
					@blur="focussing = false"
					@typing="TypingInputItems">
					<template #selected="{ items }">
						<span class="tag is-rounded is-medium is-primary" v-for="(r, i) in items" :key="i">
							<span class="mr-2">{{ r?.Text }}</span>
							<a
								role="button"
								aria-label="Close tag"
								tabindex="0"
								class="delete is-small"
								@click="RemoveRow(GetRowIndex(row.key, r))"></a>
						</span>
					</template>
				</o-inputitems>
				<o-button
					icon-left="chevron-up"
					inverted
					class="mr-2 ac-uncollapse"
					v-show="focussing"></o-button>
				<o-button icon-left="minus" @click="RemoveInputItems(row)"></o-button>
			</o-field>
		</div>
		<div class="newFilter">
			<div v-if="options.length > 1" class="mt-4">
				<o-field label="Add filter">
					<o-select v-model="selector" placeholder="Select option">
						<option v-for="option in options" :key="option.Id" :value="option">
							{{ option?.Text }}
						</option>
					</o-select>
					<o-autocomplete
						v-model="text"
						:data="acOptionsFiltered"
						:debounceTyping="300"
						field="Text"
						open-on-focus
						expanded
						@focus="acfocussing = true"
						@blur="acfocussing = false"
						@typing="LoadAcOptions"
						@select="SelectRow"
						v-if="selector != null">
						<template v-slot:empty>No results</template>
					</o-autocomplete>
					<o-button
						icon-left="chevron-up"
						inverted
						class="ac-uncollapse"
						v-show="acfocussing"></o-button>
				</o-field>
			</div>
		</div>
	</div>
</template>
<script>
	import { ref, computed, watch, inject, onMounted, nextTick } from "vue";

	export default {
		props: {
			modelValue: {
				type: Array,
				default: () => {
					return [];
				}
			},
			type: {
				type: String,
				default: undefined
			},
			data: {
				type: Array,
				default: undefined
			}
		},
		emits: ["update:modelValue", "change"],
		setup(props, context) {
			const $http = inject("$http");

			const selector = ref();
			const text = ref("");
			const options = ref([]);
			const acOptions = ref([]);
			const filters = ref(props.modelValue || []);
			const focussing = ref(false);
			const acfocussing = ref(false);

			const typing = ref("");
			const inputOptions = ref([]);
			const focussedInputRow = ref(null);
			const rows = computed(() => {
				return filters.value.reduce((acc, n) => {
					if (acc.some((row) => row.key.Id === n.key.Id)) {
						const i = acc.findIndex((row) => row.key.Id === n.key.Id);
						acc[i].values.push(n.value);
					} else {
						acc.push({ key: n.key, values: [n.value] });
					}
					return acc;
				}, []);
			});
			const awaitOptions = new Promise((resolve) => {
				const iterations = 0;
				const poll = setInterval(() => {
					if (options.value.length > 0 || iterations >= 100) {
						clearInterval(poll);
						resolve(options.value);
					}
				}, 100);
			});

			const UpdateModel = () => {
				context.emit("update:modelValue", filters.value);
				context.emit("change");
			};

			//Takes in an index from rows.value.
			const GetRowIndex = (key, val) => {
				return filters.value.findIndex((x) => x.key.Id === key.Id && x.value.Id === val.Id);
			};

			const SelectRow = (row) => {
				if (row) {
					const copy = filters.value.find((o) => {
						return o.key.Text === selector.value.Text && o.value.Id === row.Id;
					});
					if (!copy) {
						filters.value.push({ key: selector.value, value: row });
					}
					nextTick(() => {
						if (options.value.length > 1) {
							selector.value = null;
							acOptions.value = [];
						}
						text.value = "";
					});

					UpdateModel();
				}
			};

			const RemoveRow = (index) => {
				filters.value.splice(index, 1);

				UpdateModel();
			};

			const ListItemFromSelection = (o) => {
				let keyId = null;
				let valueId = null;
				let keyText = null;
				let valueText = null;

				if (o.RegionID) {
					keyId = "RegionId";
					keyText = "Region";
					valueId = o.RegionID;
					valueText = o.Region.Name;
				} else if (o.LicenseID) {
					keyId = "LicenseId";
					keyText = "License";
					valueId = o.LicenseID;
					valueText = o.License.Name;
				} else if (o.LocationID) {
					keyId = "LocationId";
					keyText = "Location";
					valueId = o.LocationID;
					valueText = o.Location.Name;
				} else if (o.CompanyID) {
					keyId = "CompanyId";
					keyText = "Company";
					valueId = o.CompanyID;
					valueText = o.Company.Name;
				} else if (o.FunctionID) {
					keyId = "FunctionId";
					keyText = "Function";
					valueId = o.FunctionID;
					valueText = o.Function.Name;
				} else if (o.SkillID) {
					keyId = "SkillId";
					keyText = "Skill";
					valueId = o.SkillID;
					valueText = o.Skill.Name;
				}

				return {
					key: {
						Id: keyId,
						Text: keyText
					},
					value: {
						Id: valueId,
						Text: valueText
					}
				};
			};

			const FormatSelections = () => {
				//does nothing if selections are in correct format
				for (let i = 0; i < filters.value.length; i++) {
					const o = filters.value[i];
					if (!o.key || !o.value) {
						filters.value[i] = ListItemFromSelection(o);
					}
				}
			};

			//Takes in an index from rows.value
			const GetOptionsFiltered = (row) => {
				if (row.key.options instanceof Array) {
					inputOptions.value = row.key.options;
					return row.key.options;
				} else {
					$http
						.get(row.key.options + "?query=" + typing.value)
						.then((resp) => {
							inputOptions.value = resp.data;
						})
						.catch((err) => {
							inputOptions.value = [];
						});
					return inputOptions.value;
				}
			};
			const FocusInputItems = (row) => {
				focussing.value = true;
				focussedInputRow.value = row;
				inputOptions.value = [];
				typing.value = "";
				GetOptionsFiltered(row);
			};
			const TypingInputItems = (text) => {
				typing.value = text;
				GetOptionsFiltered(focussedInputRow.value);
			};

			const AddInputItems = (item, row) => {
				if (filters.value.some((x) => x.key.Id === row.key.Id)) {
					if (!filters.value.some((x) => x.value.Id === item.Id)) {
						filters.value.push({ key: row.key, value: item });
						UpdateModel();
					}
				}
			};
			const RemoveInputItems = (row) => {
				filters.value = filters.value.filter((x) => x.key.Id !== row.key.Id);
				UpdateModel();
			};

			const GetAcOptions = () => {
				$http
					.get(selector.value.options + "?query=" + text.value)
					.then((resp) => {
						acOptions.value = resp.data;
					})
					.catch((err) => {
						acOptions.value = [];
					});
			};

			const LoadAcOptions = () => {
				if (selector.value) {
					if (selector.value.options) {
						if (selector.value.options instanceof Array) {
							acOptions.value = selector.value.options;
						} else {
							GetAcOptions();
						}
					} else if (selector.value.isBool === true || selector.value.isBool === false) {
						acOptions.value = [
							{ Id: true, Text: "True" },
							{ Id: false, Text: "False" }
						];
					}
				}
			};

			const BindModelOptions = () => {
				if (filters.value) {
					filters.value.forEach((row) => {
						if (!row.options) {
							const filterOption = options.value.find((x) => x.Id === row["key"]["Id"]);
							row["key"].options = filterOption.options;
						}
					});
				}
			};

			const acOptionsFiltered = computed(() => {
				return acOptions.value.filter(
					(option) => option.Text // && option.Text.toString().startsWith(text.value)
				);
			});

			const filtersOrdered = computed(() => {
				const copy = [...filters.value];
				return copy.sort((a, b) => {
					if (a.Id < b.Id) {
						return -1;
					} else {
						return 1;
					}
				});
			});

			if (props.type) {
				import("./searchFilterOptions/" + props.type + ".json").then(({ default: data }) => {
					options.value = data;
					if (options.value.length === 1) {
						selector.value = options.value[0];
						LoadAcOptions();
					}
					FormatSelections();
				});
			} else if (props.data) {
				options.value = JSON.parse(JSON.stringify(props.data));
			}

			watch(
				() => selector.value,
				(n) => {
					if (n) {
						LoadAcOptions();
					}
				}
			);

			watch(
				() => props.modelValue,
				(n, o) => {
					if (n !== o) {
						filters.value = props.modelValue;
						awaitOptions.then((options) => {
							BindModelOptions();
							//console.log(filters.value);
						});
					}
				}
			);

			return {
				selector,
				text,
				options,
				acOptions,
				inputOptions,
				typing,
				rows,
				filters,
				focussing,
				acfocussing,
				acOptionsFiltered,
				filtersOrdered,
				FocusInputItems,
				TypingInputItems,
				AddInputItems,
				GetRowIndex,
				SelectRow,
				RemoveRow,
				LoadAcOptions,
				GetOptionsFiltered,
				RemoveInputItems
			};
		}
	};
</script>
<style lang="scss">
	.searchFilter {
		width: 100%;
		& .taginput.control {
			width: 100%;
		}
	}

	.newFilter {
		width: 100%;
		& .select.control select {
			min-width: 220px;
			width: 100%;
		}
		& .autocomplete.control {
			width: 100%;
		}
	}
</style>
